import { useState } from 'react'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import dayjs from 'dayjs'
import {
  Alert,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import InfoSection from '@/components/ui/sections/InfoSection'
import { Pregnancy } from '@/core/models/pregnancy.model'
import { useGetPregnanciesByPatientQuery, useDeletePregnancyMutation } from '@/core/services/pregnancy'

import { PregnancyDialog } from './PregnancyDialog'

type PregnancySectionProps = {
  patientId: string
  readOnly?: boolean
}

const statusColor: Record<string, 'success' | 'info' | 'default'> = {
  active: 'success',
  delivered: 'info',
  ended: 'default',
}

const statusLabel: Record<string, () => string> = {
  active: () => t`Active`,
  delivered: () => t`Delivered`,
  ended: () => t`Ended`,
}

export function PregnancySection({ patientId, readOnly }: PregnancySectionProps) {
  useLingui()

  const { data: pregnancies = [], isLoading } = useGetPregnanciesByPatientQuery(patientId, { skip: !patientId })
  const [deletePregnancy] = useDeletePregnancyMutation()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingPregnancy, setEditingPregnancy] = useState<Pregnancy | null>(null)

  const handleAdd = () => {
    setEditingPregnancy(null)
    setDialogOpen(true)
  }

  const handleEdit = (pregnancy: Pregnancy) => {
    setEditingPregnancy(pregnancy)
    setDialogOpen(true)
  }

  const handleDelete = async (pregnancy: Pregnancy) => {
    await deletePregnancy({ id: pregnancy._id, patientId })
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingPregnancy(null)
  }

  // Sort: active first, then by date descending
  const sorted = [...pregnancies].sort((a, b) => {
    if (a.status === 'active' && b.status !== 'active') return -1
    if (a.status !== 'active' && b.status === 'active') return 1
    return new Date(b.lmpDate).getTime() - new Date(a.lmpDate).getTime()
  })

  return (
    <>
      <InfoSection
        action={
          !readOnly && (
            <Tooltip title={t`Add`}>
              <IconButton onClick={handleAdd} size="small">
                <AddRoundedIcon />
              </IconButton>
            </Tooltip>
          )
        }
        label={t`Pregnancy`}
      >
        {isLoading ? null : sorted.length === 0 ? (
          <Alert icon={false} severity="info" variant="outlined" sx={{ mt: '10px' }}>
            {t`No pregnancy recorded`}
          </Alert>
        ) : (
          <TableContainer component={Paper} sx={{ mt: '16px' }}>
            <Table size="small">
              <TableBody>
                {sorted.map((pregnancy) => (
                  <TableRow key={pregnancy._id}>
                    <TableCell>
                      <Chip label={statusLabel[pregnancy.status]?.()} color={statusColor[pregnancy.status]} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {t`LMP`}: {dayjs(pregnancy.lmpDate).format('DD/MM/YYYY')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {pregnancy.expectedDueDate && (
                        <Typography variant="body2">
                          {t`Due`}: {dayjs(pregnancy.expectedDueDate).format('DD/MM/YYYY')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {pregnancy.notes && (
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>
                          {pregnancy.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {!readOnly && (
                        <Stack display="inline-flex" direction="row" spacing={0.5}>
                          <Tooltip title={t`Edit`}>
                            <IconButton onClick={() => handleEdit(pregnancy)} size="small">
                              <EditRoundedIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t`Delete`}>
                            <IconButton onClick={() => handleDelete(pregnancy)} size="small">
                              <DeleteRoundedIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfoSection>

      <PregnancyDialog open={dialogOpen} onClose={handleCloseDialog} patientId={patientId} pregnancy={editingPregnancy} />
    </>
  )
}
